import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Brain, Clock, Trophy, Users as UsersIcon, FileText, Activity as ActivityIcon,
  ArrowUpRight, Flame, Snowflake,
} from "lucide-react";

const RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function fmtRelative(iso) {
  if (!iso) return "never";
  const ts = new Date(iso).getTime();
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function scoreChip(score) {
  if (score >= 70) return { label: "Hot", Icon: Flame,    classes: "bg-rose-500/15 text-rose-500 border-rose-500/40" };
  if (score >= 40) return { label: "Warm", Icon: ActivityIcon, classes: "bg-amber-500/15 text-amber-500 border-amber-500/40" };
  if (score >= 15) return { label: "Active", Icon: ActivityIcon, classes: "bg-sky-500/15 text-sky-500 border-sky-500/40" };
  return { label: "Cold", Icon: Snowflake, classes: "bg-muted text-muted-foreground border-border" };
}

export default function EngagementTab({ roomId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/rooms/${roomId}/engagement`, { params: { days } })
      // Rename the destructured response field so it doesn't shadow the
      // outer `data` state — also silences the lint's stale-deps warning.
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [roomId, days]);

  const topUser = data?.per_user?.[0];
  const topDoc  = data?.per_doc?.[0];

  // Build matrix lookup
  const cellMap = useMemo(() => {
    const m = {};
    (data?.matrix?.cells || []).forEach((c) => {
      m[`${c.user_id}|${c.doc_id}`] = c.total_sec;
    });
    return m;
  }, [data]);

  const userById = useMemo(() => {
    const m = {};
    (data?.per_user || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [data]);
  const docById = useMemo(() => {
    const m = {};
    (data?.per_doc || []).forEach((d) => { m[d.doc_id] = d; });
    return m;
  }, [data]);

  // Max for heat intensity
  const maxCell = Math.max(1, ...(data?.matrix?.cells || []).map((c) => c.total_sec));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-muted-foreground inline-flex items-center gap-2">
            <Brain className="w-3.5 h-3.5" /> — Deal intelligence
          </div>
          <h2 className="font-display text-2xl tracking-tight mt-1">Engagement</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Time spent in every document by every viewer (admins included).
            Each viewer gets a 0-100 engagement score from time, breadth,
            recency, and consistency — surface the warmest leads before a
            bid deadline.
          </p>
        </div>
        <div className="flex border border-border rounded-md overflow-hidden">
          {RANGES.map((r) => (
            <button
              key={r.days}
              data-testid={`engagement-range-${r.label}`}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
                days === r.days ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >Last {r.label}</button>
          ))}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total time watched"
          value={fmtDuration(data?.totals?.total_sec)}
          tone="text-primary bg-primary/10"
          Icon={Clock}
        />
        <StatTile
          label="Most engaged"
          value={topUser ? topUser.name : "—"}
          sub={topUser ? `${fmtDuration(topUser.total_sec)} · score ${topUser.score}` : "No activity"}
          tone="text-rose-500 bg-rose-500/10"
          Icon={Trophy}
        />
        <StatTile
          label="Most-read document"
          value={topDoc ? topDoc.name : "—"}
          sub={topDoc ? `${fmtDuration(topDoc.total_sec)} across ${topDoc.viewer_count} viewer${topDoc.viewer_count===1?"":"s"}` : "—"}
          tone="text-sky-500 bg-sky-500/10"
          Icon={FileText}
        />
        <StatTile
          label="Avg session length"
          value={fmtDuration(data?.totals?.avg_session_sec)}
          sub={`${data?.totals?.session_count || 0} session${(data?.totals?.session_count || 0) === 1 ? "" : "s"}`}
          tone="text-emerald-500 bg-emerald-500/10"
          Icon={ActivityIcon}
        />
      </div>

      {loading && !data ? (
        <div className="border border-border bg-card p-12 text-center text-sm text-muted-foreground">
          Loading engagement data…
        </div>
      ) : (data?.per_user?.length || 0) === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Brain className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="font-display text-xl mt-4">No engagement yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Once members start opening documents, time-on-document and engagement
            scores will appear here. Each open-viewer pings every 20 seconds.
          </p>
        </div>
      ) : (
        <>
          {/* Viewer leaderboard */}
          <div className="border border-border bg-card rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="label-mono text-muted-foreground">— Viewer leaderboard</div>
              <span className="mono text-[10px] text-muted-foreground">
                {data.per_user.length} viewer{data.per_user.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-[minmax(220px,1.4fr)_90px_90px_110px_120px_110px] label-mono text-muted-foreground border-b border-border bg-secondary/60 min-w-[760px]">
                <div className="px-4 py-2.5">Viewer</div>
                <div className="px-4 py-2.5 text-right">Time</div>
                <div className="px-4 py-2.5 text-right">Docs</div>
                <div className="px-4 py-2.5 text-right">Sessions</div>
                <div className="px-4 py-2.5">Last active</div>
                <div className="px-4 py-2.5 text-right">Score</div>
              </div>
              {data.per_user.map((u) => {
                const chip = scoreChip(u.score);
                return (
                  <div
                    key={u.user_id}
                    data-testid={`engagement-user-${u.user_id}`}
                    className="grid grid-cols-[minmax(220px,1.4fr)_90px_90px_110px_120px_110px] border-b border-border last:border-b-0 min-w-[760px] hover:bg-secondary/30 transition"
                  >
                    <div className="px-4 py-3 text-sm min-w-0">
                      <div className="font-medium truncate">{u.name}</div>
                      <div className="mono text-[10px] text-muted-foreground truncate">
                        {u.email}
                        <span className={`ml-2 px-1.5 py-0.5 rounded uppercase ${u.role === "admin" ? "bg-amber-500/15 text-amber-600" : "bg-secondary"}`}>{u.role}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3 mono text-xs text-right">{fmtDuration(u.total_sec)}</div>
                    <div className="px-4 py-3 mono text-xs text-right">{u.doc_count}</div>
                    <div className="px-4 py-3 mono text-xs text-right">{u.session_count}</div>
                    <div className="px-4 py-3 mono text-xs text-muted-foreground">{fmtRelative(u.last_active)}</div>
                    <div className="px-4 py-3 flex items-center justify-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${chip.classes}`}>
                        <chip.Icon className="w-3 h-3" /> {chip.label}
                      </span>
                      <span className="mono text-xs w-7 text-right tabular-nums">{u.score}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per-document time table */}
          <div className="border border-border bg-card rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="label-mono text-muted-foreground">— Documents by time spent</div>
              <span className="mono text-[10px] text-muted-foreground">
                {data.per_doc.length} document{data.per_doc.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-[minmax(260px,1.6fr)_100px_100px_110px_130px_40px] label-mono text-muted-foreground border-b border-border bg-secondary/60 min-w-[780px]">
                <div className="px-4 py-2.5">Document</div>
                <div className="px-4 py-2.5 text-right">Total time</div>
                <div className="px-4 py-2.5 text-right">Viewers</div>
                <div className="px-4 py-2.5 text-right">Avg session</div>
                <div className="px-4 py-2.5">Last opened</div>
                <div className="px-4 py-2.5"></div>
              </div>
              {data.per_doc.map((d) => {
                const max = data.per_doc[0]?.total_sec || 1;
                const pct = Math.round((d.total_sec / max) * 100);
                return (
                  <div
                    key={d.doc_id}
                    data-testid={`engagement-doc-${d.doc_id}`}
                    className="grid grid-cols-[minmax(260px,1.6fr)_100px_100px_110px_130px_40px] border-b border-border last:border-b-0 min-w-[780px] hover:bg-secondary/30 transition"
                  >
                    <div className="px-4 py-3 text-sm min-w-0">
                      <div className="font-medium truncate">{d.name}</div>
                      <div className="mt-1 h-1 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="px-4 py-3 mono text-xs text-right">{fmtDuration(d.total_sec)}</div>
                    <div className="px-4 py-3 mono text-xs text-right">{d.viewer_count}</div>
                    <div className="px-4 py-3 mono text-xs text-right">{fmtDuration(d.avg_session_sec)}</div>
                    <div className="px-4 py-3 mono text-xs text-muted-foreground">{fmtRelative(d.latest_view)}</div>
                    <div className="px-4 py-3 text-right">
                      <Link
                        to={`/app/rooms/${roomId}/view/${d.doc_id}`}
                        className="text-primary hover:underline inline-flex items-center"
                        title="Open document"
                      >
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Heatmap matrix */}
          {(data.matrix?.user_ids?.length || 0) > 0 && (data.matrix?.doc_ids?.length || 0) > 0 && (
            <div className="border border-border bg-card rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="label-mono text-muted-foreground">— Heatmap (top viewers × top documents)</div>
                <span className="mono text-[10px] text-muted-foreground">darker = more time</span>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="border-separate border-spacing-0.5">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-medium p-2 sticky left-0 bg-card z-[1]">Viewer / Document</th>
                      {data.matrix.doc_ids.map((did) => (
                        <th key={did} className="p-2">
                          <div className="text-[10px] mono text-muted-foreground rotate-[-45deg] origin-bottom-left whitespace-nowrap w-6">
                            {(docById[did]?.name || "—").slice(0, 18)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.matrix.user_ids.map((uid) => (
                      <tr key={uid}>
                        <td className="text-xs px-2 py-1 sticky left-0 bg-card whitespace-nowrap truncate max-w-[220px]">
                          {userById[uid]?.name || userById[uid]?.email || "—"}
                        </td>
                        {data.matrix.doc_ids.map((did) => {
                          const sec = cellMap[`${uid}|${did}`] || 0;
                          const intensity = sec / maxCell; // 0..1
                          // bg from primary alpha
                          const alpha = sec === 0 ? 0 : Math.max(0.1, intensity);
                          return (
                            <td
                              key={did}
                              title={`${userById[uid]?.name || ""} · ${docById[did]?.name || ""} — ${fmtDuration(sec)}`}
                              className="w-8 h-8 text-center align-middle border border-border"
                              style={{ backgroundColor: `hsl(var(--primary) / ${alpha})` }}
                            >
                              {sec > 0 && (
                                <span className="mono text-[9px] text-foreground/80">
                                  {sec >= 60 ? `${Math.floor(sec/60)}m` : `${sec}s`}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Score legend */}
          <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Brain className="w-3.5 h-3.5" />
              <span>Engagement score = 50pt time + 20pt breadth + 20pt recency + 10pt consistency</span>
            </div>
            <div className="flex items-center gap-3">
              {[{lab:"Hot", c:"bg-rose-500"},{lab:"Warm", c:"bg-amber-500"},{lab:"Active",c:"bg-sky-500"},{lab:"Cold",c:"bg-muted-foreground"}].map((b) => (
                <span key={b.lab} className="inline-flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${b.c}`} /> {b.lab}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, sub, tone, Icon }) {
  return (
    <div className="border border-border bg-card rounded-md p-4 flex items-center gap-3 min-w-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-xl tracking-tight truncate" title={value}>{value}</div>
        <div className="label-mono text-muted-foreground truncate">{label}</div>
        {sub && <div className="text-[10px] mono text-muted-foreground truncate mt-0.5" title={sub}>{sub}</div>}
      </div>
    </div>
  );
}
